import React from 'react';
import { Box, Text, useColorModeValue, useDisclosure, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { useReduxDispatch } from '../../../app/store';
import { SwipeableSEHomeCard } from '../../../common/components/SwipeableSEHomeCard';
import { DeleteIcon, EditIcon } from '../../../styles/custom-icons';
import { selectEvChargerData } from '../dashboard/evChargerSlice';
import { Schedule } from '../types';
import { formatScheduleTime } from './scheduler-helper';
import { deleteEvChargerSchedule, selectEvChargerSchedule } from './schedulerSlice';

type Props = {
  groupId: string;
  onEdit: (groupId: string) => void;
};

/* istanbul ignore next -- @preserve */
export function ScheduleCard({ groupId, onEdit }: Props) {
  const dispatch = useReduxDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();
  const { siteDeviceId } = useSelector(selectEvChargerData);
  const schedule: Schedule | null = useSelector(selectEvChargerSchedule(groupId));
  const toast = useToast();
  const textColor = useColorModeValue('customGrey.700', 'customGrey.400');

  async function handleDeleteSchedule() {
    const resultAction = await dispatch(deleteEvChargerSchedule({ siteDeviceId, groupId }));
    if (deleteEvChargerSchedule.rejected.match(resultAction)) {
      Sentry.captureException(resultAction.error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          component: 'ScheduleCard',
          method: 'handleDeleteSchedule',
        },
      });
      toast({
        title: t('Common.something went wrong'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
        isClosable: true,
      });
    }
  }

  if (!schedule) return null;

  const isNextDay = schedule.startTime && schedule.endTime ? schedule.endTime < schedule.startTime : false;

  return (
    <>
      <SwipeableSEHomeCard
        data-testid={`schedule-summary-${groupId}`}
        width="100%"
        mb={3}
        borderTopLeftRadius={5}
        primaryButtonProps={{
          onClick: () => {
            onOpen();
          },
          'data-testid': `delete-schedule-button-${groupId}`,
          children: (
            <>
              <DeleteIcon w={8} h={8} />
              <Text mt={1} fontSize="xs">
                {t('Common.delete').toUpperCase()}
              </Text>
            </>
          ),
        }}
        secondaryButtonProps={{
          onClick: () => {
            onEdit(groupId);
          },
          'data-testid': `edit-schedule-button-${groupId}`,
          children: (
            <>
              <EditIcon w={8} h={8} />
              <Text mt={1} fontSize="xs">
                {t('Common.edit').toUpperCase()}
              </Text>
            </>
          ),
        }}
      >
        <Box>{schedule.name}</Box>
        <Text data-testid={`schedule-${groupId}-turn-on-time`} fontSize="sm" color={textColor}>
          {t('Devices.turn on')}: {formatScheduleTime(schedule.startTime)}
        </Text>
        <Text data-testid={`schedule-${groupId}-turn-off-time`} fontSize="sm" color={textColor}>
          {t('Devices.turn off')}: {formatScheduleTime(schedule.endTime)} {isNextDay ? ' Next Day' : null}
        </Text>
      </SwipeableSEHomeCard>

      <AlertDialogModal
        isOpen={isOpen}
        onClose={onClose}
        header={t('EVSE.delete schedule?')}
        subHeader={t('EVSE.delete schedule confirmation')}
        confirmButtonName={t('Common.delete')}
        onConfirm={handleDeleteSchedule}
      />
    </>
  );
}
